import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getOrder, updateOrder, getOrderClients, addWorkPart, getWorkResources } from '../services/orders';
import ClientPicker from '../components/common/ClientPicker';
import './OrderDetailPage.css';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('es-ES') : '—');
const formatHours = (hours) => `${Number(hours || 0).toFixed(2).replace('.', ',')} h`;
const formatAmount = (amount) => (
  amount > 0 ? amount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : '—'
);

// Hay descripciones de más de 600 caracteres: sin colapsar empujan los partes
// de trabajo fuera de pantalla, que es justo lo que se viene a ver.
const MAX_DESC_LINES = 6;

// LAKPED-16.6: motivos de que no se facture todo lo trabajado. Son los del prototipo de Paula,
// para que la oficina no tenga que escribirlos a mano cada vez.
const MOTIVOS = ['Preparación', 'Pieza rehecha', 'Espera de material', 'Embalaje / descarga', 'Avería', 'Otro'];

const SOURCE_LABELS = {
  access: 'Importado de Access',
  manual: 'Creado a mano',
  email: 'Recibido por email',
  paper: 'Escaneado en papel',
  offer: 'Generado desde una oferta',
};

const OrderDetailPage = () => {
  const { orderHash } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Vuelve a la lista tal como estaba (página, búsqueda y filtro); si se ha entrado
  // por enlace directo no hay nada que restaurar y se abre la lista limpia.
  const backToList = () => navigate(`/orders${location.state?.from || ''}`);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  // Apuntar un parte es lo que alimenta la calculadora: sin esto solo aprende de lo importado
  const [addingPart, setAddingPart] = useState(false);
  const [resources, setResources] = useState([]);
  const [newPart, setNewPart] = useState(null);
  const [albaran, setAlbaran] = useState('');
  const [cerrando, setCerrando] = useState(false);
  const [guardandoAlbaran, setGuardandoAlbaran] = useState(false);

  const loadOrder = useCallback(async () => {
    setLoading(true);
    try {
      setOrder(await getOrder(orderHash));
      setError(null);
    } catch (err) {
      setOrder(null);
      setError(err.response?.status === 404 ? 'Este pedido no existe' : 'No se ha podido cargar el pedido');
    } finally {
      setLoading(false);
    }
  }, [orderHash]);

  useEffect(() => { loadOrder(); }, [loadOrder]);
  useEffect(() => { setAlbaran(order?.delivery_note || ''); }, [order?.delivery_note]);

  const startEditing = () => {
    setForm({
      client: order.client?.name || '',
      client_reference: order.client_reference || '',
      order_date: order.order_date ? order.order_date.slice(0, 10) : '',
      description: order.description || '',
      invoiced_amount: order.invoiced_amount || '',
      notes: order.notes || '',
    });
    setEditing(true);
    if (clients.length === 0) getOrderClients().then(setClients).catch(() => {});
  };

  const saveChanges = async (extra = {}) => {
    setSaving(true);
    try {
      const matched = clients.find((c) => c.name.toLowerCase() === (form.client || '').trim().toLowerCase());
      await updateOrder(orderHash, {
        client_hash: matched?.hash,
        client_reference: form.client_reference || null,
        order_date: form.order_date ? `${form.order_date}T00:00:00` : null,
        description: form.description || null,
        invoiced_amount: form.invoiced_amount === '' ? null : Number(form.invoiced_amount),
        notes: form.notes || null,
        ...extra,
      });
      await loadOrder();
      setEditing(false);
    } catch (err) {
      setError('No se han podido guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const startPart = () => {
    const hoy = new Date().toISOString().slice(0, 10);
    setNewPart({ code: '', resource_hash: '', work_date: hoy, hours: '', billable_hours: '', notes: '' });
    if (resources.length === 0) getWorkResources(true).then(setResources).catch(() => {});
  };

  const savePart = async () => {
    setAddingPart(true);
    setError(null);
    try {
      await addWorkPart(orderHash, {
        code: newPart.code || null,
        resource_hash: newPart.resource_hash || null,
        work_date: newPart.work_date ? `${newPart.work_date}T00:00:00` : null,
        hours: Number(newPart.hours),
        billable_hours: newPart.billable_hours === '' ? null : Number(newPart.billable_hours),
        notes: newPart.notes || null,
      });
      setNewPart(null);
      await loadOrder();
    } catch (err) {
      setError('No se ha podido guardar el parte');
    } finally {
      setAddingPart(false);
    }
  };

  const guardarAlbaran = async () => {
    setGuardandoAlbaran(true);
    try {
      await updateOrder(orderHash, { delivery_note: albaran.trim() || null });
      await loadOrder();
    } catch (err) {
      setError('No se ha podido guardar el nº de albarán');
    } finally {
      setGuardandoAlbaran(false);
    }
  };

  const toggleClosed = async () => {
    setSaving(true);
    try {
      await updateOrder(orderHash, { closed: !order.closed });
      await loadOrder();
    } catch (err) {
      setError('No se ha podido cambiar el estado del pedido');
    } finally {
      setSaving(false);
    }
  };

  const descLines = useMemo(
    () => (order?.description || '').split('\n').filter(Boolean).length,
    [order?.description]
  );
  const descIsLong = descLines > MAX_DESC_LINES;

  // El importe del pedido sale de sus líneas; si vienen del Access y no tienen, del facturado.
  const importeLineas = useMemo(() => (order?.lines || []).reduce(
    (suma, l) => suma + ((l.unit_price ?? l.offered_price ?? 0) * (l.quantity || 1)), 0
  ), [order?.lines]);
  const importePedido = importeLineas || order?.invoiced_amount || 0;
  const noFacturables = Math.max(0, (order?.total_hours || 0) - (order?.billable_hours || 0));
  // Lo ofertado sale de las propias líneas: cada una guarda el precio que ofertamos,
  // así no hace falta volver a pedir la oferta para comparar.
  const importeOfertado = useMemo(() => (order?.lines || []).reduce(
    (suma, l) => suma + ((l.offered_price || 0) * (l.quantity || 1)), 0
  ), [order?.lines]);
  const desviacion = importeOfertado
    ? Math.round(((importePedido - importeOfertado) / importeOfertado) * 1000) / 10
    : null;

  const diferenciaParte = newPart && newPart.billable_hours !== ''
    ? Math.max(0, Number(newPart.hours || 0) - Number(newPart.billable_hours || 0))
    : 0;
  const porcentajeFacturable = order?.total_hours
    ? Math.round((order.billable_hours / order.total_hours) * 100)
    : 0;

  if (loading) return <div className="order-detail"><p className="order-loading">Cargando…</p></div>;

  if (error) {
    return (
      <div className="order-detail">
        <button className="order-back" onClick={backToList}>‹ Volver a pedidos</button>
        <div className="order-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="order-detail">
      <button className="order-back" onClick={backToList}>‹ Volver a pedidos</button>

      <div className="order-head">
        <div>
          <h1 className="order-client">{order.client?.name || 'Sin cliente'}</h1>
          <p className="order-subtitle">
            {order.client_reference ? `Referencia ${order.client_reference}` : 'Sin referencia'}
            {' · '}{formatDate(order.order_date)}
          </p>
        </div>
        <div className="order-head-actions">
          <span className={`order-badge ${order.closed ? 'cerrado' : 'abierto'}`}>
            {order.closed ? 'Cerrado' : 'Abierto'}
          </span>
          {!editing && (
            <>
              <button className="order-action" onClick={() => window.print()}>Imprimir hoja de pedido</button>
              <button className="order-action" onClick={startEditing} disabled={saving}>Editar</button>
              <button
                className="order-action"
                onClick={() => (order.closed ? toggleClosed() : setCerrando(true))}
                disabled={saving}
              >
                {order.closed ? 'Reabrir' : 'Cerrar pedido'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="order-cards">
        <div className="order-card">
          <span className="order-card-label">Horas trabajadas</span>
          <span className="order-card-value">{formatHours(order.total_hours)}</span>
          <span className="order-card-hint">
            {order.parts_count} parte{order.parts_count === 1 ? '' : 's'} de trabajo
          </span>
        </div>
        <div className="order-card">
          <span className="order-card-label">Horas a facturar</span>
          <span className="order-card-value">{formatHours(order.billable_hours)}</span>
          <span className="order-card-hint">
            {noFacturables > 0
              ? `${formatHours(noFacturables)} no facturables`
              : 'Se factura todo lo trabajado'}
          </span>
        </div>
        <div className="order-card">
          <span className="order-card-label">Importe del pedido</span>
          <span className="order-card-value">{formatAmount(importePedido)}</span>
          {order.invoiced_amount > 0 && importeLineas > 0 && (
            <span className="order-card-hint">Facturado {formatAmount(order.invoiced_amount)}</span>
          )}
          {order.subcontract_cost > 0 && (
            <span className="order-card-hint">Subcontrata {formatAmount(order.subcontract_cost)}</span>
          )}
        </div>
        <div className="order-card">
          <span className="order-card-label">Origen</span>
          <span className="order-card-value origen">{SOURCE_LABELS[order.source] || order.source}</span>
          {order.external_id && <span className="order-card-hint">Nº {order.external_id} en Access</span>}
          {order.offer_hash && (
            <button className="order-card-link" onClick={() => navigate(`/offers/${order.offer_hash}`)}>
              Ver la oferta
            </button>
          )}
        </div>
      </div>

      {editing && (
        <section className="order-section">
          <h2>Editar pedido</h2>
          <div className="order-edit-grid">
            <div className="order-edit-field order-edit-wide">
              <label>Cliente</label>
              <ClientPicker id="order-cliente" clients={clients} value={form.client}
                            onChange={(nombre) => setForm({ ...form, client: nombre })} />
            </div>
            <div className="order-edit-field">
              <label>Referencia del cliente</label>
              <input value={form.client_reference}
                     onChange={(e) => setForm({ ...form, client_reference: e.target.value })} />
            </div>
            <div className="order-edit-field">
              <label>Fecha</label>
              <input type="date" value={form.order_date}
                     onChange={(e) => setForm({ ...form, order_date: e.target.value })} />
            </div>
            <div className="order-edit-field">
              <label>Importe facturado</label>
              <input type="number" step="0.01" value={form.invoiced_amount}
                     onChange={(e) => setForm({ ...form, invoiced_amount: e.target.value })} />
            </div>
            <div className="order-edit-field order-edit-full">
              <label>Trabajo</label>
              <textarea rows="3" value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="order-edit-field order-edit-full">
              <label>Notas</label>
              <textarea rows="2" value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="order-edit-actions">
            <button className="order-cancel" onClick={() => setEditing(false)} disabled={saving}>Cancelar</button>
            <button className="order-save" onClick={() => saveChanges()} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </section>
      )}

      {!editing && (
      <section className="order-section">
        <h2>{(order.lines || []).length > 0 ? 'Líneas del pedido' : 'Trabajo'}</h2>
        {/* LAKPED-16.5: los pedidos nuevos traen una línea por artículo; los del Access, su texto. */}
        {(order.lines || []).length > 0 ? (
          <table className="order-lineas">
            <thead>
              <tr>
                <th className="der">Cant.</th>
                <th>Artículo</th>
                <th className="der">Precio pedido</th>
                <th className="der">Precio ofertado</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((linea, i) => (
                <tr key={i}>
                  <td className="der">{linea.quantity ?? '—'}</td>
                  <td>{linea.part_code || '—'}</td>
                  <td className="der">{formatAmount(linea.unit_price)}</td>
                  <td className="der">{formatAmount(linea.offered_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
        <p className={`order-description ${descIsLong && !descExpanded ? 'collapsed' : ''}`}>
          {order.description || 'Sin descripción'}
        </p>
        )}
        {descIsLong && (order.lines || []).length === 0 && (
          <button className="order-desc-toggle" onClick={() => setDescExpanded(!descExpanded)}>
            {descExpanded ? 'Ver menos' : `Ver las ${descLines} líneas`}
          </button>
        )}
        {order.notes && <p className="order-notes">{order.notes}</p>}
      </section>
      )}

      <section className="order-section">
        <h2>Plano</h2>
        {order.analysis ? (
          <button className="order-plano-link" onClick={() => navigate(`/analysis/${order.analysis.hash}`)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
            </svg>
            {order.analysis.filename}
          </button>
        ) : (
          <p className="order-sin-plano">
            Sin plano asociado
            {order.document_path && <span className="order-doc-path">{order.document_path}</span>}
          </p>
        )}
      </section>

      {/* LAKPED-16.6: el albarán es lo que engancha el pedido con lo facturado. Se teclea a mano:
          cada albarán de ProSicar lleva el nº de pedido del cliente. */}
      <section className="order-section order-section-albaran">
        <h2>Albarán</h2>
        <div className="order-albaran">
          <input
            value={albaran}
            onChange={(e) => setAlbaran(e.target.value)}
            placeholder="Nº de albarán"
            disabled={guardandoAlbaran}
          />
          <button
            className="order-action"
            onClick={guardarAlbaran}
            disabled={guardandoAlbaran || albaran.trim() === (order.delivery_note || '')}
          >
            {guardandoAlbaran ? 'Guardando…' : 'Guardar'}
          </button>
          {order.delivery_note && (
            <span className="order-albaran-ok">Relacionado con el albarán {order.delivery_note}</span>
          )}
        </div>
      </section>

      <section className="order-section order-section-partes">
        <div className="order-parts-head">
          <h2>Partes de trabajo</h2>
          {!order.closed && (
            <button className="order-action" onClick={startPart}>Apuntar parte</button>
          )}
        </div>

        {order.parts.length === 0 ? (
          <p className="order-sin-plano">Todavía no hay partes para este pedido</p>
        ) : (
          <>
            <div className="order-parts-wrapper">
              <table className="order-parts">
                <thead>
                  <tr>
                    <th>Nº parte</th>
                    <th>Operario</th>
                    <th>Fecha fin</th>
                    <th className="col-num">Trabajado</th>
                    <th className="col-num">A facturar</th>
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {order.parts.map((part) => (
                    <tr key={part.hash}>
                      <td className="col-codigo">{part.code || '—'}</td>
                      <td>{part.resource?.name || '—'}</td>
                      <td>{formatDate(part.work_date)}</td>
                      <td className="col-num">{formatHours(part.hours)}</td>
                      <td className="col-num">
                        {formatHours(part.billable_hours ?? part.hours)}
                      </td>
                      <td>{part.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3">Total</td>
                    <td className="col-num">{formatHours(order.total_hours)}</td>
                    <td className="col-num">{formatHours(order.billable_hours)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="order-facturable">
              <div className="order-facturable-barra">
                <i style={{ width: `${porcentajeFacturable}%` }} />
              </div>
              <p>
                {formatHours(order.billable_hours)} de {formatHours(order.total_hours)} se facturan
                ({porcentajeFacturable} %). Sirve para medir la productividad real.
              </p>
            </div>
          </>
        )}
      </section>

      {/* Al cerrar se enseña lo que se sabe de verdad: lo ofertado contra lo que vale el pedido
          y las horas. El importe del albarán vendría de ProSicar y hoy no lo tenemos. */}
      {cerrando && (
        <div className="order-modal-fondo" onClick={() => !saving && setCerrando(false)}>
          <div className="order-modal order-modal-cierre" onClick={(e) => e.stopPropagation()}>
            <h2>Cerrar el pedido</h2>
            <p className="order-modal-sub">Esto es lo que ha salido frente a lo que se ofertó.</p>

            <dl className="order-cierre">
              <div>
                <dt>Ofertado</dt>
                <dd>{importeOfertado > 0 ? formatAmount(importeOfertado) : 'Sin oferta vinculada'}</dd>
              </div>
              <div>
                <dt>Pedido</dt>
                <dd>{formatAmount(importePedido)}</dd>
              </div>
              <div>
                <dt>Desviación</dt>
                <dd className={desviacion ? 'desvio' : ''}>
                  {desviacion === null ? '—' : `${desviacion > 0 ? '+' : ''}${String(desviacion).replace('.', ',')} %`}
                </dd>
              </div>
              <div>
                <dt>Horas trabajadas</dt>
                <dd>{formatHours(order.total_hours)}</dd>
              </div>
              <div>
                <dt>Horas a facturar</dt>
                <dd>{formatHours(order.billable_hours)}</dd>
              </div>
              <div>
                <dt>Albarán</dt>
                <dd>{order.delivery_note || 'Sin anotar'}</dd>
              </div>
            </dl>

            {!order.delivery_note && (
              <p className="order-modal-aviso">
                Sin el nº de albarán no se podrá enganchar este pedido con lo facturado.
              </p>
            )}

            <div className="order-part-acciones">
              <button className="order-action" onClick={() => setCerrando(false)} disabled={saving}>
                Cancelar
              </button>
              <button
                className="order-part-save"
                onClick={async () => { await toggleClosed(); setCerrando(false); }}
                disabled={saving}
              >
                {saving ? 'Cerrando…' : 'Cerrar pedido'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apuntar el parte sin salir del pedido: lo pidió Paula para no ir adelante y atrás */}
      {newPart && (
        <div className="order-modal-fondo" onClick={() => !addingPart && setNewPart(null)}>
          <div className="order-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Apuntar parte de trabajo</h2>
            <p className="order-modal-sub">
              {order.client?.name || 'Sin cliente'}
              {order.client_reference ? ` · Pedido ${order.client_reference}` : ''}
            </p>

            <div className="order-modal-campos">
              <div className="order-part-field">
                <label htmlFor="parte-codigo">Nº de parte</label>
                <input id="parte-codigo" value={newPart.code}
                       onChange={(e) => setNewPart({ ...newPart, code: e.target.value })} />
              </div>
              <div className="order-part-field">
                <label htmlFor="parte-operario">Operario</label>
                <select id="parte-operario" value={newPart.resource_hash}
                        onChange={(e) => setNewPart({ ...newPart, resource_hash: e.target.value })}>
                  <option value="">Sin indicar</option>
                  {resources.map((r) => <option key={r.hash} value={r.hash}>{r.name}</option>)}
                </select>
              </div>
              <div className="order-part-field">
                <label htmlFor="parte-fecha">Fecha fin</label>
                <input id="parte-fecha" type="date" value={newPart.work_date}
                       onChange={(e) => setNewPart({ ...newPart, work_date: e.target.value })} />
              </div>
              <div className="order-part-field">
                <label htmlFor="parte-horas">Tiempo trabajado</label>
                <input id="parte-horas" type="number" min="0" step="0.25" value={newPart.hours}
                       placeholder="0,00"
                       onChange={(e) => setNewPart({ ...newPart, hours: e.target.value })} />
                <span className="order-part-opt">Todo lo invertido, también lo que no se cobra.</span>
              </div>
              <div className="order-part-field">
                <label htmlFor="parte-facturable">Tiempo a facturar</label>
                <input id="parte-facturable" type="number" min="0" step="0.25"
                       value={newPart.billable_hours}
                       placeholder={newPart.hours || '0,00'}
                       onChange={(e) => setNewPart({ ...newPart, billable_hours: e.target.value })} />
                <span className="order-part-opt">En blanco se factura todo lo trabajado.</span>
              </div>
              <div className="order-part-field">
                <label>Diferencia</label>
                <input value={formatHours(diferenciaParte)} readOnly className="order-part-readonly" />
              </div>
            </div>

            {diferenciaParte > 0 && (
              <p className="order-modal-aviso">
                El tiempo a facturar es menor que el trabajado: indica el motivo.
              </p>
            )}

            <div className="order-part-field order-modal-ancho">
              <label htmlFor="parte-notas">Observaciones / motivo de la diferencia</label>
              <input id="parte-notas" value={newPart.notes}
                     onChange={(e) => setNewPart({ ...newPart, notes: e.target.value })} />
              <div className="order-motivos">
                {MOTIVOS.map((motivo) => (
                  <button key={motivo} type="button" className="order-motivo"
                          onClick={() => setNewPart({
                            ...newPart,
                            notes: newPart.notes ? `${newPart.notes}. ${motivo}` : motivo,
                          })}>
                    {motivo}
                  </button>
                ))}
              </div>
            </div>

            <div className="order-part-acciones">
              <button className="order-action" onClick={() => setNewPart(null)} disabled={addingPart}>
                Cancelar
              </button>
              <button className="order-part-save" onClick={savePart}
                      disabled={addingPart || !(Number(newPart.hours) > 0)}>
                {addingPart ? 'Guardando…' : 'Guardar parte'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage;
